/**
 * Date Formatting Utilities with Timezone Support
 * Provides consistent date formatting across the application using user's preferred timezone
 */

/**
 * Default date formatting options
 */
const DEFAULT_DATE_OPTIONS = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

const DEFAULT_DATETIME_OPTIONS = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

const DEFAULT_TIME_OPTIONS = {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

/**
 * Format a UTC date in the specified timezone
 * @param {string|Date} utcDate - UTC date string or Date object
 * @param {string|null} timezoneId - IANA timezone identifier (e.g., "America/New_York"). Null uses browser default.
 * @param {Intl.DateTimeFormatOptions} options - Formatting options
 * @returns {string} Formatted date string, or 'N/A' if date is invalid
 */
export function formatDateInTimezone(utcDate, timezoneId = null, options = {}) {
  if (!utcDate) return 'N/A';

  try {
    const date = new Date(utcDate);
    if (isNaN(date.getTime())) return 'N/A';

    // Use provided timezone or fall back to browser's timezone
    const tz = timezoneId || Intl.DateTimeFormat().resolvedOptions().timeZone;

    const mergedOptions = {
      ...DEFAULT_DATETIME_OPTIONS,
      ...options,
      timeZone: tz,
    };

    return new Intl.DateTimeFormat('en-US', mergedOptions).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
}

/**
 * Format a UTC date as a date only (no time)
 * @param {string|Date} utcDate - UTC date string or Date object
 * @param {string|null} timezoneId - IANA timezone identifier
 * @param {Intl.DateTimeFormatOptions} options - Additional formatting options
 * @returns {string} Formatted date string
 */
export function formatDateOnly(utcDate, timezoneId = null, options = {}) {
  if (!utcDate) return 'N/A';

  try {
    const date = new Date(utcDate);
    if (isNaN(date.getTime())) return 'N/A';

    // Date-only values use UTC to prevent timezone-induced day shifting.
    // A "date-only" value has no meaningful time component, so timezone conversion
    // would incorrectly shift the date backwards for western timezones.
    const tz = 'UTC';

    const mergedOptions = {
      ...DEFAULT_DATE_OPTIONS,
      ...options,
      timeZone: tz,
    };

    return new Intl.DateTimeFormat('en-US', mergedOptions).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
}

/**
 * Parse a date-only UTC string into a local Date object preserving the calendar date.
 * Useful when you need a Date object (e.g. for react-big-calendar) rather than a formatted string.
 * @param {string} utcDateStr - UTC date string (e.g. "2026-02-16T00:00:00.000Z")
 * @returns {Date|null} Local Date with the same year/month/day as the UTC date, or null
 */
export function parseDateOnlyAsLocal(utcDateStr) {
  if (!utcDateStr) return null;
  const d = new Date(utcDateStr);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * Format a UTC date as time only
 * @param {string|Date} utcDate - UTC date string or Date object
 * @param {string|null} timezoneId - IANA timezone identifier
 * @param {Intl.DateTimeFormatOptions} options - Additional formatting options
 * @returns {string} Formatted time string
 */
export function formatTimeOnly(utcDate, timezoneId = null, options = {}) {
  // Bare time-only values (e.g. "08:00" or "14:30:00" from an <input type="time">)
  // have no date and no timezone — new Date() can't parse them and would yield "N/A".
  // Format them directly to 12-hour h:mm AM/PM. (TASK-0213)
  if (typeof utcDate === 'string') {
    const bareTime = utcDate.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (bareTime) {
      const hours = parseInt(bareTime[1], 10);
      const minutes = bareTime[2];
      if (isNaN(hours) || hours > 23 || Number(minutes) > 59) return 'N/A';
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 === 0 ? 12 : hours % 12;
      return `${displayHours}:${minutes} ${period}`;
    }
  }
  return formatDateInTimezone(utcDate, timezoneId, {
    ...DEFAULT_TIME_OPTIONS,
    ...options,
  });
}

/**
 * Format a date relative to now (e.g., "2 hours ago", "in 3 days")
 * @param {string|Date} utcDate - UTC date string or Date object
 * @param {string|null} timezoneId - IANA timezone identifier (used for formatting fallback)
 * @returns {string} Relative time string
 */
export function formatRelativeTime(utcDate, timezoneId = null) {
  if (!utcDate) return 'N/A';

  try {
    const date = new Date(utcDate);
    if (isNaN(date.getTime())) return 'N/A';

    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    // For recent times, use relative format
    if (diffSecs < 60) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }

    // For older dates, use full date format
    return formatDateInTimezone(utcDate, timezoneId);
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'N/A';
  }
}

/**
 * Format a date for display in submission tables
 * @param {string|Date} utcDate - UTC date string or Date object
 * @param {string|null} timezoneId - IANA timezone identifier
 * @returns {string} Formatted date string suitable for tables
 */
export function formatSubmissionDate(utcDate, timezoneId = null) {
  return formatDateInTimezone(utcDate, timezoneId, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Get today's (or offset) date as a YYYY-MM-DD string in the given timezone.
 * Unlike new Date().toISOString().split('T')[0], this returns the correct
 * calendar date for the user's timezone instead of converting to UTC first.
 * @param {string|null} timezoneId - IANA timezone identifier. Null uses browser default.
 * @param {number} offsetDays - Days to add (e.g., 1 for tomorrow, 7 for next week)
 * @returns {string} Date string in YYYY-MM-DD format
 */
export function getLocalDateString(timezoneId = null, offsetDays = 0) {
  const tz = timezoneId || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const d = new Date();
  if (offsetDays) d.setDate(d.getDate() + offsetDays);
  // en-CA locale formats as YYYY-MM-DD which matches HTML date input format
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/**
 * Get the browser's default timezone
 * @returns {string} IANA timezone identifier
 */
export function getBrowserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format timezone for display (e.g., "America/New_York" -> "America/New York")
 * @param {string} timezoneId - IANA timezone identifier
 * @returns {string} Human-readable timezone name
 */
export function formatTimezoneDisplay(timezoneId) {
  if (!timezoneId) return 'Browser Default';
  return timezoneId.replace(/_/g, ' ');
}

export default {
  formatDateInTimezone,
  formatDateOnly,
  parseDateOnlyAsLocal,
  formatTimeOnly,
  formatRelativeTime,
  formatSubmissionDate,
  getLocalDateString,
  getBrowserTimezone,
  formatTimezoneDisplay,
};
